// @/components/reusable/avatar.jsx
// User avatar component with initials display
// Supports layout variants via sidebar-theme.js config

import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { appStore } from "@/services/app/store";
import { authService } from "@/services/auth";
import { useLayout } from "@/components/layout/main";
import { getTheme } from "@/config/sidebar-theme";

// ============================================================================
// SIZE CONFIG
// ============================================================================

const sizes = {
  sm: { container: "size-8", text: "text-xs" },
  md: { container: "size-10", text: "text-xs" },
  lg: { container: "size-12", text: "text-sm" },
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Extract user data from auth state or cache
 */
function extractUserData(authState, sessionCache) {
  // Try auth service first
  if (authState?.authenticated && authState.user) {
    const user = authState.user;
    return {
      firstName: user.firstName || user.name?.split(" ")[0] || "",
      lastName: user.lastName || user.name?.split(" ").slice(1).join(" ") || "",
      email: user.email || "",
    };
  }

  // Try session cache
  if (sessionCache) {
    try {
      const profile = JSON.parse(sessionCache);
      return {
        firstName: profile.firstName || profile.name?.split(" ")[0] || "",
        lastName: profile.lastName || profile.name?.split(" ").slice(1).join(" ") || "",
        email: profile.email || "",
      };
    } catch {
      // Ignore parse errors
    }
  }

  return null;
}

/**
 * Generate initials from user data
 */
function getInitials(firstName, lastName, email) {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  if (firstName) {
    return firstName.charAt(0);
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return "U";
}

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * Avatar - Displays user initials in a styled container
 */
export function Avatar({ size = "md", className }) {
  const { layout = "default" } = useLayout();
  const theme = getTheme(layout);
  const [initials, setInitials] = useState("...");

  useEffect(() => {
    const updateInitials = () => {
      const authState = authService.getAuthState();
      const sessionCache = sessionStorage.getItem("userProfileCache");
      const userData = extractUserData(authState, sessionCache);

      if (userData) {
        setInitials(getInitials(userData.firstName, userData.lastName, userData.email));
      } else {
        setInitials("U");
      }
    };

    updateInitials();

    window.addEventListener("authStateChange", updateInitials);
    return () => window.removeEventListener("authStateChange", updateInitials);
  }, []);

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <div
      className={clsx(
        "rounded-2xl flex items-center justify-center",
        theme.avatar.container,
        sizeConfig.container,
        className
      )}
    >
      <span className={clsx("font-medium", theme.avatar.text, sizeConfig.text)}>
        {initials}
      </span>
    </div>
  );
}

/**
 * AvatarWithInfo - Avatar with user name and company name
 */
export function AvatarWithInfo({ size = "md", className }) {
  const { layout = "default" } = useLayout();
  const theme = getTheme(layout);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
  });
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const currentUserEmail = authService.getAuthState()?.user?.email?.toLowerCase();

    const getUserData = () => {
      const authState = authService.getAuthState();
      const sessionCache = sessionStorage.getItem("userProfileCache");
      const extracted = extractUserData(authState, sessionCache);

      if (extracted) {
        setUserData((prev) => ({ ...prev, ...extracted }));
        setHasData(true);
        return true;
      }
      return false;
    };

    const updateCompanyData = (profileData) => {
      if (!profileData) return;
      try {
        let companyData = profileData.CompanyData || profileData.COMPANYDATA;
        if (typeof companyData === "string") {
          companyData = JSON.parse(companyData);
        }
        setUserData((prev) => ({
          ...prev,
          companyName: companyData?.Name || "",
        }));
      } catch {
        // Ignore parse errors
      }
    };

    getUserData();

    // Check store for company profile
    // NOTE: Legacy localStorage fallback (appState_companyProfile) removed
    // as it's cleaned up during initialization and no longer reliable
    const profileData = appStore.state.data.companyProfile;
    if (profileData) updateCompanyData(profileData);

    // Check users list
    if (currentUserEmail && appStore.state.data.users?.list?.length > 0) {
      const currentUser = appStore.state.data.users.list.find(
        (user) => user.email?.toLowerCase() === currentUserEmail
      );
      if (currentUser) {
        setUserData((prev) => ({
          ...prev,
          firstName: currentUser.firstName || currentUser.name?.split(" ")[0] || "",
          lastName: currentUser.lastName || currentUser.name?.split(" ").slice(1).join(" ") || "",
          email: currentUser.email || "",
        }));
        setHasData(true);
      }
    }

    // Subscribe to updates
    const unsubscribeCompany = appStore.subscribe("companyProfile", (data) => {
      if (data) updateCompanyData(data);
    });

    const unsubscribeUsers = appStore.subscribe("users", (data) => {
      if (data?.list && currentUserEmail) {
        const currentUser = data.list.find(
          (user) => user.email?.toLowerCase() === currentUserEmail
        );
        if (currentUser) {
          setUserData((prev) => ({
            ...prev,
            firstName: currentUser.firstName || currentUser.name?.split(" ")[0] || "",
            lastName: currentUser.lastName || currentUser.name?.split(" ").slice(1).join(" ") || "",
            email: currentUser.email || prev.email,
          }));
          setHasData(true);
        }
      }
    });

    const handleAuthChange = () => getUserData();
    window.addEventListener("authStateChange", handleAuthChange);

    return () => {
      unsubscribeCompany?.();
      unsubscribeUsers?.();
      window.removeEventListener("authStateChange", handleAuthChange);
    };
  }, []);

  const displayInitials = getInitials(userData.firstName, userData.lastName, userData.email);

  const displayName = userData.firstName && userData.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : hasData
      ? userData.email || "User"
      : "...";

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <span className={clsx("flex min-w-0 items-center gap-3", className)}>
      <div
        className={clsx(
          "shrink-0 rounded-2xl flex items-center justify-center -ml-1",
          theme.avatar.container,
          sizeConfig.container
        )}
      >
        <span className={clsx("font-semibold", theme.avatar.text, sizeConfig.text)}>
          {hasData ? displayInitials : "..."}
        </span>
      </div>
      <span className="min-w-0 text-left flex flex-col">
        <h4 className={clsx("block tracking-tighter", theme.avatar.name)}>
          {displayName}
        </h4>
        <span className={clsx("block truncate text-xs", theme.avatar.company)}>
          {hasData ? userData.companyName || "Company" : "..."}
        </span>
      </span>
    </span>
  );
}

export default Avatar;
